import { useEffect, useId, useState, type FormEvent } from 'react';
import { Card } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { FieldError } from '../../components/ui/FieldError';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { frenetDepositStatusBadgeVariant, frenetDepositStatusPt } from '../../utils/enums-ptbr';
import { formatCentsToBRL } from '../../utils/money';
import { getMercadoPagoCheckoutRedirectUrl } from './mercadoPagoCheckout';
import type { UseFrenetDepositResult } from './useFrenetDeposit';

interface FrenetDepositSectionProps {
  deposit: UseFrenetDepositResult;
}

/**
 * "Adicionar saldo" (TTP-270) — Frenet-only, rendered by `ProvidersPage.tsx`
 * right below the Frenet `ProviderConnectCard` (only when that connection
 * is `active`), never inside `ProviderConnectCard.tsx` itself: every other
 * provider has no equivalent action, and threading Frenet-only props deep
 * into that shared, onboarding-reused component would blur its scope (same
 * "don't force provider-specific behavior onto a shared surface" reasoning
 * `frenet.adapter.ts#createDeposit`'s own docstring gives for NOT putting
 * this on `ShippingAdapter`).
 *
 * The value is entered in REAIS (a lojista-facing form, not a wire payload)
 * and converted to cents only at submit time — same "cents on the wire,
 * reais at the presentation layer" boundary `money.ts`'s own docstring
 * documents, applied in reverse here.
 *
 * Redirects the WHOLE page to Mercado Pago's classic checkout
 * (`mercadoPagoCheckout.ts`) the instant a fresh `preferenceId` comes back
 * from `start()` — no intermediate click, no inline SDK/Bricks render (see
 * that module's docstring for why the Wallet Brick doesn't work here).
 * `deposit.clearPreference()` is never called from this component in that
 * path: the redirect navigates away before a user could act on this screen
 * again, and Frenet's own `CallbackUrls` bring them back to
 * `/providers?deposit=success|failure|pending` (`ProvidersPage.tsx`), a
 * fresh mount with `preferenceId` back to `null`.
 */
export function FrenetDepositSection({ deposit }: FrenetDepositSectionProps) {
  const inputId = useId();
  const [valueInput, setValueInput] = useState('');
  const [formError, setFormError] = useState<string | undefined>(undefined);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reais = Number(valueInput.replace(',', '.'));
    if (!Number.isFinite(reais) || reais <= 0) {
      setFormError('Informe um valor maior que zero.');
      return;
    }
    setFormError(undefined);
    void deposit.start(Math.round(reais * 100));
  }

  useEffect(() => {
    if (!deposit.preferenceId) {
      return;
    }
    window.location.href = getMercadoPagoCheckoutRedirectUrl(deposit.preferenceId);
  }, [deposit.preferenceId]);

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-foreground">Adicionar saldo na Frenet</span>
        <p className="text-xs text-muted-foreground">
          Abre o checkout do Mercado Pago da própria Frenet — o pagamento vai direto para a Frenet, o Trivio nunca
          recebe ou retém esse valor.
        </p>
      </div>

      {deposit.latest ? (
        <div className="flex items-center justify-between gap-4 border border-border bg-muted/20 p-3">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Último depósito</span>
            <span className="text-foreground">{formatCentsToBRL(deposit.latest.value_cents)}</span>
          </div>
          <Badge variant={frenetDepositStatusBadgeVariant(deposit.latest.status)}>
            {frenetDepositStatusPt(deposit.latest.status)}
          </Badge>
        </div>
      ) : null}

      {deposit.latest?.status === 'pending' ? (
        <Alert variant="info" title="Depósito em processamento">
          A liberação é assíncrona — o saldo pode demorar a refletir mesmo depois do pagamento confirmado. Recarregue
          o saldo do provedor acima em alguns instantes.
        </Alert>
      ) : null}

      {deposit.error ? (
        <Alert variant="error" title="Não foi possível iniciar o depósito">
          {deposit.error}
        </Alert>
      ) : null}

      {deposit.preferenceId ? (
        <LoadingState label="Redirecionando para o checkout do Mercado Pago…" />
      ) : (
        <form className="flex flex-col gap-2 sm:flex-row sm:items-start" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor={inputId}>Valor (R$)</Label>
            <Input
              id={inputId}
              type="number"
              min={0.01}
              step="0.01"
              inputMode="decimal"
              value={valueInput}
              invalid={Boolean(formError)}
              aria-describedby={formError ? `${inputId}-error` : undefined}
              onChange={(event) => setValueInput(event.target.value)}
              disabled={deposit.starting}
            />
            <FieldError id={`${inputId}-error`} message={formError} />
          </div>
          <Button type="submit" variant="outline" disabled={deposit.starting} className="sm:mt-6">
            {deposit.starting ? 'Preparando checkout…' : 'Adicionar saldo'}
          </Button>
        </form>
      )}
    </Card>
  );
}
